import { useMemo } from 'react';
import { compact } from 'lodash-es';
import { Accounting } from 'anchor-scout-common/types';
import { useStore } from '~/contexts';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { getAccountingDetails } from '~/integrations/Accounting/accounting.utils';
import { serviceBillingModel, serviceCostModel, serviceModel } from '~/models';
import { selectors } from '~/store/state-selectors';
import { AdvancedSetting } from '../../ServiceAdvancedSettings.types';
const accountingName = getAccountingDetails(Accounting.QBO).fullName;
export const useGetSettings = (param)=>{
    let { isFreshlyAdded, isApprovedRelationship, service, now, effectiveDate } = param;
    const { PREAPPROVED_AMOUNT_IMPROVMENTS, SERVICE_SETTINGS_SELECTORS = false } = useFeatureFlags();
    const showFinancialCentsIntegration = useStore(selectors.integration.showFinancialCentsIntegration);
    const showKarbonIntegration = useStore(selectors.integration.showKarbonIntegration);
    const showQboIntegration = useStore(selectors.integration.showQboIntegration);
    const hasQboClasses = useStore((state)=>state.qboClasses.data);
    const allowToCustomizeQboClasses = useStore(selectors.integration.allowToCustomizeQboClasses);
    return useMemo(()=>{
        const isDraft = !isApprovedRelationship;
        const isFreshlyAddedOrDraft = isDraft || isFreshlyAdded;
        const isOngoing = serviceBillingModel.isOngoing(serviceModel.billingTrigger(service));
        const isBilledUpfront = serviceModel.isBilledUpfront(service);
        const isSkipBillingOnAcceptance = serviceModel.isSkipBillingOnAcceptance(service);
        const isRecurring = serviceBillingModel.isRecurring(serviceModel.billingTrigger(service));
        const isOnDemand = serviceBillingModel.isOnDemand(serviceModel.billingTrigger(service));
        const isPaused = serviceModel.isPaused(service);
        const isAwaitingApproval = serviceModel.isAwaitingApproval(service.status);
        const hasFinancialCentsIntegration = serviceModel.hasFinancialCentsIntegration(service);
        const hasKarbonIntegration = serviceModel.hasKarbonIntegration(service);
        const startBillingAt = serviceModel.startBillingAt(service);
        let prorateIsDisabled;
        const canProrate = !(isBilledUpfront && isSkipBillingOnAcceptance) && !isPaused || !!startBillingAt;
        return compact([
            isFreshlyAddedOrDraft && isAwaitingApproval && isOngoing && canProrate && {
                type: AdvancedSetting.Prorate,
                label: 'Prorate the first payment',
                info: 'When enabled, the price of the first invoice will be calculated in proportion to the number of days the agreement was in effect during the billing cycle',
                disabled: prorateIsDisabled,
                disabledTooltip: 'Services with automatic billing set to paused cannot be prorated',
                optionName: 'First payment proration'
            },
            isAwaitingApproval && !isApprovedRelationship && {
                type: AdvancedSetting.Optional,
                label: SERVICE_SETTINGS_SELECTORS ? 'Mark as optional' : 'Mark this service as optional',
                info: 'Mark a service as optional if you would like to give your client the option to include the service in this agreement',
                optionName: 'Optional service'
            },
            isAwaitingApproval && (isRecurring || isOnDemand) && {
                type: AdvancedSetting.LimitCharges,
                label: 'Bill a limited number of times',
                info: 'Set a limit on how many times this service can be billed',
                optionName: 'Number of billing cycles'
            },
            serviceModel.supportsPriceIncrease(service) && {
                type: AdvancedSetting.AutoPriceIncrease,
                label: 'Set an automatic annual price increase',
                info: 'Automatic price increase will be applied once a year unless manually changed or cancelled.',
                optionName: 'Annual price increase'
            },
            !isApprovedRelationship && {
                type: AdvancedSetting.LimitedTimeDiscountOffer,
                label: 'Apply discount only if the proposal is accepted within the deadline',
                disabled: !service.cost.discount,
                info: 'The discount specified for this service will apply only if the proposal is accepted before the deadline. If accepted after the deadline, the discount will be automatically removed',
                disabledTooltip: 'To set a condition for the discount, please add a discount to the service first.',
                optionName: 'Conditional discount'
            },
            isAwaitingApproval && serviceBillingModel.isRecurring(service.billing) && {
                type: AdvancedSetting.LimitedTimeDiscountOfferForAgreement,
                label: 'Expire discount after a set period',
                disabled: !service.cost.discount,
                info: "Discount applies for the set period after signing, then it's removed automatically.",
                disabledTooltip: 'To set an automatic expiration for the discount, you must first add a discount to the service.',
                optionName: 'Discount expiration'
            },
            showQboIntegration && {
                type: AdvancedSetting.QboClasses,
                label: "Override ".concat(accountingName, " class assignment"),
                info: "Set the class of this service when syncing invoices to ".concat(accountingName, "."),
                disabled: !allowToCustomizeQboClasses || !!(hasQboClasses && !hasQboClasses.length),
                disabledTooltip: "To override the ".concat(accountingName, " class assignment, classes must first be enabled and defined in ").concat(accountingName, "."),
                optionName: 'QuickBooks Online class override'
            },
            showFinancialCentsIntegration && !isApprovedRelationship && {
                type: AdvancedSetting.FinancialCents,
                label: 'Override team member assignment in Financial Cents',
                info: 'Select the team members to be assigned to the items generated by this service in Financial Cents once the proposal has been approved.',
                disabled: !hasFinancialCentsIntegration,
                disabledTooltip: 'To override team member assignments, the service needs to be mapped to a template in Financial Cents.',
                optionName: 'Financial Cents assignee override'
            },
            showKarbonIntegration && !isApprovedRelationship && {
                type: AdvancedSetting.Karbon,
                label: 'Override team member assignment in Karbon',
                info: 'Select the team members to be assigned to the items generated by this service in Karbon once the proposal has been approved.',
                disabled: !hasKarbonIntegration || !service.integrations.karbonIntegratedIds().length,
                disabledTooltip: 'To override team member assignments, the service needs to be mapped to a template in Karbon.',
                optionName: 'Karbon assignee override'
            },
            PREAPPROVED_AMOUNT_IMPROVMENTS && serviceCostModel.isVariablePrice(service.cost) && {
                type: AdvancedSetting.PreapprovedAmount,
                label: 'Set a preapproved amount',
                info: "Charging a client up to the preapproved amount for the period doesn't require their approval. If no amount is set, or if the charge exceeds the limit, client approval will be required.",
                optionName: 'Preapproved amount'
            }
        ]);
    }, [
        isFreshlyAdded,
        isApprovedRelationship,
        service,
        now,
        effectiveDate
    ]);
};
